import { Link, createFileRoute } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import { EmptyState, PageHeader } from '@/components/page-header';
import { VatStatusBadge, VatTabs } from '@/components/vat';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatTrn } from '@/lib/clients';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useT } from '@/lib/i18n';
import { useOrgId } from '@/lib/org';
import { negate, useVatReturns, useVatSettings, type VatReturnSummary } from '@/lib/vat';

export const Route = createFileRoute('/taxes/vat/')({ component: VatCentre });

const QUARTERLY = [
  'taxPages.vatHome.quarterlyFromJanuary',
  'taxPages.vatHome.quarterlyFromFebruary',
  'taxPages.vatHome.quarterlyFromMarch',
] as const;

/** Screen T1 (document 06): Taxes › VAT, return cards per period, then the history. */
function VatCentre() {
  const t = useT();
  const orgId = useOrgId();
  const settings = useVatSettings();
  const returns = useVatReturns();
  const s = settings.data;
  const list = returns.data ?? [];
  const active = list.filter((r) => r.status !== 'paid');
  const history = list.filter((r) => r.status === 'paid');

  return (
    <>
      <PageHeader
        title={t('taxPages.vatShared.title')}
        description={t('taxPages.vatHome.description')}
      />
      <VatTabs />
      {!orgId ? (
        <EmptyState title={t('shell.selectClient')} hint={t('home.selectClientHint')} />
      ) : settings.isPending || returns.isPending ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : settings.isError || returns.isError ? (
        <EmptyState
          title={t('taxPages.vatHome.loadError')}
          hint={(settings.error ?? returns.error)?.message}
        />
      ) : !s?.vatRegistered && list.length === 0 ? (
        <EmptyState
          title={t('taxPages.vatHome.notRegisteredTitle')}
          hint={t('taxPages.vatHome.notRegisteredHint')}
          action={
            <Button asChild variant="outline">
              <Link to="/settings/company">{t('taxPages.vatHome.companySettings')}</Link>
            </Button>
          }
        />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-x-8 gap-y-1 rounded-lg border bg-card px-4 py-3 text-sm">
            <span>
              <span className="text-muted-foreground">{t('taxPages.vatShared.trnLabel')} </span>
              <span className="tnum font-medium">{formatTrn(s?.trn ?? null) || '—'}</span>
            </span>
            <span>
              <span className="text-muted-foreground">
                {t('taxPages.vatShared.filingFrequency')}{' '}
              </span>
              <span className="font-medium">
                {s?.frequency === 'monthly'
                  ? t('taxPages.vatShared.monthly')
                  : t(QUARTERLY[(s?.quarterStartMonth ?? 1) - 1] ?? QUARTERLY[0])}
              </span>
            </span>
            {s?.registrationDate ? (
              <span>
                <span className="text-muted-foreground">
                  {t('taxPages.vatHome.registeredSince')}{' '}
                </span>
                <span className="font-medium">{displayDate(s.registrationDate)}</span>
              </span>
            ) : null}
          </div>
          {active.length ? (
            <div className="mb-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {active.map((r) => (
                <ReturnCard key={r.key} r={r} currency={s?.currency ?? 'AED'} />
              ))}
            </div>
          ) : null}
          <h2 className="mb-2 text-base font-semibold">{t('taxPages.vatHome.filedAndPaid')}</h2>
          {history.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('taxPages.vatHome.noPaidReturns')}</p>
          ) : (
            <div className="rounded-lg border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('taxPages.vatHome.colPeriod')}</TableHead>
                    <TableHead className="w-[120px]">{t('taxPages.vatHome.colFiled')}</TableHead>
                    <TableHead className="w-[120px]">{t('taxPages.vatHome.colDue')}</TableHead>
                    <TableHead className="w-[150px] text-end">
                      {t('taxPages.vatHome.colNetVat')}
                    </TableHead>
                    <TableHead className="w-[90px]">{t('taxPages.vatHome.colStatus')}</TableHead>
                    <TableHead className="w-[120px] text-end">
                      {t('taxPages.vatHome.colAction')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell>
                        <div className="font-medium">{r.label}</div>
                        <div className="text-xs text-muted-foreground">
                          {displayDate(r.periodStart)} – {displayDate(r.periodEnd)}
                        </div>
                      </TableCell>
                      <TableCell>{r.filedOn ? displayDate(r.filedOn) : ''}</TableCell>
                      <TableCell>{displayDate(r.dueDate)}</TableCell>
                      <TableCell className="tnum text-end">
                        {s?.currency} {formatAmount(r.netVat)}
                      </TableCell>
                      <TableCell>
                        <VatStatusBadge r={r} />
                      </TableCell>
                      <TableCell className="text-end">
                        <Button asChild size="sm" variant="link" className="px-0">
                          <Link to="/taxes/vat/returns/$key" params={{ key: r.key }}>
                            {t('taxPages.vatHome.viewReturn')}
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </>
  );
}

function ReturnCard({ r, currency }: { r: VatReturnSummary; currency: string }) {
  const t = useT();
  const refund = r.netVat.startsWith('-');
  const action =
    r.status === 'filed'
      ? refund
        ? t('taxPages.vatShared.recordRefund')
        : t('taxPages.vatShared.recordPayment')
      : r.status === 'open'
        ? t('taxPages.vatHome.viewRunningTotals')
        : t('taxPages.vatHome.prepareReturn');
  return (
    <section className="flex flex-col rounded-lg border bg-card p-4" data-testid="vat-card">
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <h2 className="text-base font-semibold">{r.label}</h2>
          <p className="text-xs text-muted-foreground">
            {displayDate(r.periodStart)} – {displayDate(r.periodEnd)} ·{' '}
            {t('taxPages.vatShared.dueOn', { date: displayDate(r.dueDate) })}
          </p>
        </div>
        <VatStatusBadge r={r} />
      </div>
      <dl className="mb-3 grid grid-cols-2 gap-y-1 text-sm">
        <dt className="text-muted-foreground">{t('taxPages.vatHome.vatOnSales')}</dt>
        <dd className="tnum text-end">{formatAmount(r.outputVat)}</dd>
        <dt className="text-muted-foreground">{t('taxPages.vatHome.vatOnPurchases')}</dt>
        <dd className="tnum text-end">{formatAmount(r.inputVat)}</dd>
        <dt className="font-medium">
          {refund
            ? t('taxPages.vatShared.netVatRefundable')
            : t('taxPages.vatShared.netVatPayable')}
        </dt>
        <dd className="tnum text-end font-semibold">
          {currency} {formatAmount(refund ? negate(r.netVat) : r.netVat)}
        </dd>
      </dl>
      {r.status === 'open' ? (
        <p className="mb-3 text-xs text-muted-foreground">{t('taxPages.vatHome.runningTotals')}</p>
      ) : null}
      {r.exceptions ? (
        <p className="mb-3 inline-flex items-center gap-1.5 text-xs text-[#B45309]">
          <AlertTriangle className="size-3.5" aria-hidden />
          {t('taxPages.vatHome.exceptionsToReview', { count: r.exceptions })}
        </p>
      ) : null}
      <div className="mt-auto">
        <Button asChild size="sm" variant={r.status === 'open' ? 'outline' : 'default'}>
          <Link to="/taxes/vat/returns/$key" params={{ key: r.key }}>
            {action}
          </Link>
        </Button>
      </div>
    </section>
  );
}
